"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="204993" className="flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-sm bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative py-2 text-sm font-default uppercase tracking-widest text-foreground transition-colors after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:text-primary hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 animate-pulse rounded-sm bg-muted" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 border-b border-border backdrop-blur-xl shadow-lg" : "bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-8">
        {}
        <Link to="#" className="flex items-center shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/eagle_47_logo.png" alt="Eagle 47 Strategic Holdings" className="h-8 w-auto object-contain brightness-110" />
        </Link>

        {}
        <MenuProvider menu_id="16">
          <DesktopMenuConsumer />
        </MenuProvider>

        {}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-primary/10 hover:text-primary rounded-sm transition-all" aria-label="Open navigation menu">
              <Menu className="size-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="dark bg-background text-foreground border-l border-border sm:max-w-sm backdrop-blur-2xl">
            <div className="mt-12 flex flex-col gap-8">
              <MenuProvider menu_id="16">
                <MobileMenuConsumer />
              </MenuProvider>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </section>;
}